import React, { useEffect, useRef, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { VoucherScanLog } from "@/components/VoucherScanLog";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
import { BranchFilter } from "@/components/BranchFilter";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { Plus, X, Trash2, AlertTriangle, Info, PackageMinus, Search, Upload, FileDown, CheckCircle2, Eye, Printer, Pencil } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { SearchableSelect } from "@/components/SearchableSelect";
import { toast, Toaster } from "sonner";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { useViewOverlay } from "@/hooks/useViewOverlay";
import { useAuth } from "@/contexts/AuthContext";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import { ReportingPeriodInput, ApprovedReportingPeriod } from "@/components/ReportingPeriodField";

const LINE_TYPE = ["Material", "Asset"];
const ISSUE_TYPE = ["Consumable", "Returnable"];

export default function IssueVoucherPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [filterBranch, setFilterBranch] = useState("");
  // Sprint 4.3 — Warehouse filter (branch-scoped)
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const params = useMemo(() => (filterBranch ? { branch_id: filterBranch } : {}), [filterBranch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/issue-vouchers", params);
  const load = reload;
  const openFormTab = useFormTab();
  const pendingIds = useMemo(() => pendingDocIds(list), [list]);
  const pendingMap = useApprovalPendingMap("issue_voucher", pendingIds);

  // Sprint 4.3 — Warehouse picker follows branch filter; reset on branch change.
  useEffect(() => {
    const url = filterBranch ? `/warehouses/picker?branch_id=${filterBranch}` : `/warehouses/picker`;
    api.get(url).then(r => setWarehouseOptions(Array.isArray(r.data) ? r.data : []))
       .catch(() => setWarehouseOptions([]));
    setFilterWarehouse("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch]);

  const closeViewToList      = () => setViewing(null);
  const closeViewToDashboard = () => { setViewing(null); navigate("/"); };

  // Jun-2026 (user request) — bulk print: select several vouchers, download
  // one combined PDF; format chosen in a modal.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPrintOpen, setBulkPrintOpen] = useState(false);
  const toggleSelected = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  /**
   * Admin-only row Delete.
   * - Draft / Submitted → direct DELETE.
   * - Approved (posted) → cancel-approval (restores batch qty + flips assets
   *   back to Available + deletes stock/asset movements + closes returnable
   *   register rows + reverses cost events), THEN DELETE.
   * - Cannot delete if any line was partially returned (backend guard on
   *   /cancel-approval). Toast surfaces the backend reason.
   */
  async function deleteVoucher(v) {
    const isPosted = v.status === "Approved" && v.posted;
    const lead = isPosted
      ? `Delete approved voucher ${v.voucher_number}?\n\n` +
        `• Batch quantities will be restored to source warehouse\n` +
        `• Assets (if any) will be flipped back to Available\n` +
        `• Returnable register + stock entries + cost events will be reversed\n` +
        `• Voucher record will then be permanently deleted\n\n` +
        `This action is irreversible. Continue?`
      : `Permanently delete voucher ${v.voucher_number}?\n\n` +
        `Stock has not been issued yet, so nothing is reversed.\n\n` +
        `Continue?`;
    if (!window.confirm(lead)) return;
    const t = toast.loading(`Deleting ${v.voucher_number}…`);
    try {
      if (isPosted) {
        await api.post(`/issue-vouchers/${v.id}/cancel-approval`);
      }
      await api.delete(`/issue-vouchers/${v.id}`);
      toast.success(
        isPosted
          ? `${v.voucher_number} deleted — material returned to stock`
          : `${v.voucher_number} deleted`,
        { id: t, duration: 6000 },
      );
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed", { id: t });
    }
  }

  const openNew = () => openFormTab({
    path: "/issue-vouchers/new",
    label: "New Issue Voucher",
    icon: Plus,
    parent: "/issue-vouchers",
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <IssueVoucherForm onViewList={viewList} onClose={close} onSaved={saved} />
    ),
  });

  // Sprint 4.11.10 — Edit an existing voucher.
  // For Approved vouchers the caller is expected to have already
  // triggered the cancel-approval reverse, so by the time we open
  // the form the voucher is back to Draft and stock has been
  // restored. PUT save handles the re-submission automatically.
  const openEdit = (v) => openFormTab({
    path: `/issue-vouchers/${v.id}/edit`,
    label: `Edit ${v.voucher_number || v.id.slice(0, 8)}`,
    icon: Plus,
    parent: "/issue-vouchers",
    onSaved: load,
    render: ({ close, saved, viewList }) => (
      <IssueVoucherForm existing={v} onViewList={viewList} onClose={close} onSaved={saved} />
    ),
  });

  // Auto-spawn "New Issue Voucher" tab on landing (preserves the "land on form" UX).
  const autoSpawnedRef = useRef(false);
  useEffect(() => {
    if (autoSpawnedRef.current) return;
    autoSpawnedRef.current = true;
    openNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = !q ? list : list.filter(v => [
      v.voucher_number, v.branch_name, v.from_warehouse_name, v.issued_to_name, v.issued_to_free_text, v.issued_to_type,
      v.ship_to_label, v.ship_to_name, v.ship_to_city,
      v.project_name, v.status, v.reference_no, (v.voucher_date || "").slice(0, 10),
    ].some(x => (x || "").toString().toLowerCase().includes(q)));
    if (filterWarehouse) {
      base = base.filter(v => (v.from_warehouse_id || "") === filterWarehouse);
    }
    // Sort by voucher number — highest first (FY-aware: "RCPL/ISV/0113/26-27" → FY "26-27" desc, then running number "0113" desc)
    const parts = (vn) => {
      const m = String(vn || "").split("/");
      const fy = m[m.length - 1] || "";          // "26-27"
      const num = parseInt(m[m.length - 2] || "0", 10) || 0; // 113
      return { fy, num };
    };
    return [...base].sort((a, b) => {
      const pa = parts(a.voucher_number), pb = parts(b.voucher_number);
      if (pa.fy !== pb.fy) return pb.fy.localeCompare(pa.fy); // newer FY first
      return pb.num - pa.num;                                 // higher running number first
    });
  }, [list, search, filterWarehouse]);

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Stores"
        title="Material / Asset Issue Voucher"
        subtitle="Issue stock from FIFO batches or assign assets to members — Consumable or Returnable."
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="bulk-upload-iv-btn" onClick={() => setBulkOpen(true)}
              className="bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2 border border-slate-300">
              <Upload size={13} /> Bulk Upload
            </button>
            <button data-testid="new-issue-voucher-btn" onClick={openNew}
              className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Plus size={14} /> New Issue Voucher
            </button>
          </div>
        }
      />

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="iv-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by voucher / project / member / status…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[320px]" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Branch</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="iv-branch-filter" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Warehouse</span>
        <select data-testid="iv-warehouse-filter" value={filterWarehouse}
          onChange={e => setFilterWarehouse(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm min-w-[180px]">
          <option value="">All warehouses</option>
          {warehouseOptions.map(w => (
            <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>
          ))}
        </select>
        <div className="ml-auto flex items-center gap-3">
          {selectedIds.size > 0 && (
            <button data-testid="bulk-print-btn" onClick={() => setBulkPrintOpen(true)}
              className="bg-slate-800 hover:bg-slate-900 text-white px-3 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <FileDown size={13} /> Bulk Print ({selectedIds.size})
            </button>
          )}
          <div className="text-xs text-slate-500">
            <span className="text-slate-400 mr-1">Showing:</span>
            <span className="font-bold text-slate-800 tabular">{filtered.length}</span>
            <span className="text-slate-400 mx-1">/</span>
            <span className="text-slate-600 tabular">{list.length}</span>
          </div>
        </div>
      </div>

      {list.length === 0 ? <EmptyState title="No issue vouchers yet" icon={PackageMinus} /> : filtered.length === 0 ? <EmptyState title="No matches" hint="Clear the search to see all vouchers." /> : (
        <Card className="overflow-hidden">
          <table className="w-full table-fixed" data-testid="iv-table">
            <colgroup>
              <col style={{ width: "3%"  }} />{/* Select */}
              <col style={{ width: "12%" }} />{/* Voucher / Date */}
              <col style={{ width: "12%" }} />{/* Branch / Warehouse */}
              <col style={{ width: "17%" }} />{/* Issued To */}
              <col style={{ width: "14%" }} />{/* Ship To */}
              <col style={{ width: "4%"  }} />{/* Lines */}
              <col style={{ width: "10%" }} />{/* Value */}
              <col style={{ width: "18%" }} />{/* Status — wide enough for the
                                                  L2/L2 · ADMIN · 3d
                                                  pending-approval badge plus
                                                  the stacked Take Action btn. */}
              <col style={{ width: "10%" }} />{/* Actions */}
            </colgroup>
            <thead className="bg-slate-50 border-b-2 border-slate-200">
              <tr>
                <th className="py-2.5 px-2 text-center">
                  <input type="checkbox" data-testid="iv-select-all"
                    className="accent-orange-600 cursor-pointer"
                    checked={filtered.length > 0 && filtered.every(v => selectedIds.has(v.id))}
                    onChange={(e) => setSelectedIds(e.target.checked
                      ? new Set(filtered.map(v => v.id)) : new Set())} />
                </th>
                {["Voucher · Date", "Branch · Warehouse", "Issued To", "Ship To", "Lines", "Value", "Status", ""].map((h, i) =>
                  <th key={h || i}
                      className={`text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2.5 px-3 ${
                        h === "Lines" ? "text-center" : h === "Value" ? "text-right" : "text-left"
                      }`}>{h}</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60 align-top">
                  <td className="py-2.5 px-2 text-center">
                    <input type="checkbox" data-testid={`iv-select-${v.id}`}
                      className="accent-orange-600 cursor-pointer"
                      checked={selectedIds.has(v.id)}
                      onChange={() => toggleSelected(v.id)} />
                  </td>
                  {/* Voucher # + date stacked */}
                  <td className="py-2.5 px-3">
                    <button data-testid={`view-iv-${v.id}`} onClick={() => setViewing(v)}
                      className="font-mono font-bold text-[11px] text-[#F97316] hover:underline break-all text-left">
                      {v.voucher_number}
                    </button>
                    <div className="text-[10px] text-slate-500 mt-0.5 tabular-nums">{(v.voucher_date || "").slice(0, 10)}</div>
                  </td>
                  {/* Branch + warehouse stacked */}
                  <td className="py-2.5 px-3 text-[11px] text-slate-700">
                    <div className="font-medium truncate" title={v.branch_name}>{v.branch_name || "—"}</div>
                    <div className="text-[10px] text-slate-500 truncate mt-0.5"
                         data-testid={`iv-wh-${v.id}`}
                         title={v.from_warehouse_name || ""}>
                      {v.from_warehouse_name
                        ? v.from_warehouse_name
                        : <span className="text-amber-600">— no warehouse</span>}
                    </div>
                  </td>
                  {/* Issued To */}
                  <td className="py-2.5 px-3">
                    <div className="text-[11px] font-medium text-slate-800 truncate"
                         title={v.issued_to_name || v.issued_to_free_text || ""}>
                      {v.issued_to_name || v.issued_to_free_text || "—"}
                    </div>
                    {v.issued_to_type && (
                      <div className="text-[10px] text-slate-400 mt-0.5">{v.issued_to_type}</div>
                    )}
                  </td>
                  {/* Ship To (label) */}
                  <td className="py-2.5 px-3 text-[11px] text-slate-700"
                      data-testid={`iv-ship-to-${v.id}`}>
                    <div className="truncate"
                         title={[v.ship_to_label, v.ship_to_name, v.ship_to_city].filter(Boolean).join(" · ")}>
                      {v.ship_to_label ? (
                        <span className="font-medium">{v.ship_to_label}</span>
                      ) : v.ship_to_name ? (
                        <span>{v.ship_to_name}</span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </div>
                    {v.ship_to_city && (
                      <div className="text-[10px] text-slate-500 truncate mt-0.5"
                           title={[v.ship_to_address, v.ship_to_city, v.ship_to_state].filter(Boolean).join(", ")}>
                        {v.ship_to_city}{v.ship_to_state ? `, ${v.ship_to_state}` : ""}
                      </div>
                    )}
                  </td>
                  {/* Lines */}
                  <td className="py-2.5 px-3 text-[11px] text-slate-600 text-center tabular-nums">
                    {(v.line_items || []).length}
                  </td>
                  {/* Value */}
                  <td className="py-2.5 px-3 text-[12px] tabular-nums font-bold text-[#F97316] text-right">
                    {formatINR(v.total_value || 0)}
                  </td>
                  {/* Status — pending-approval badge + inline Take Action button */}
                  <td className="py-2.5 px-3 overflow-hidden">
                    <div className="inline-flex flex-col items-start gap-1.5 max-w-full">
                      <PendingApprovalBadge status={v.status} pendingInfo={pendingMap[v.id]} />
                      {pendingMap[v.id] && (
                        <TakeActionButton pendingInfo={pendingMap[v.id]} testid={`take-action-iv-${v.id}`} />
                      )}
                    </div>
                  </td>
                  {/* Actions — icon-only for compactness. Always visible
                      regardless of voucher status (Draft, Pending, Approved). */}
                  <td className="py-2.5 px-3 text-right whitespace-nowrap">
                    <button data-testid={`export-iv-${v.id}`}
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setExporting({ ...v, _kind: "issue", _anchor: { bottom: window.innerHeight - r.top + 6, right: window.innerWidth - r.right } });
                      }}
                      title="Export PDF (works on Draft / Pending / Approved)"
                      className="text-slate-500 hover:text-[#F97316] p-1 inline-flex">
                      <FileDown size={14} />
                    </button>
                    <button onClick={() => setViewing(v)}
                      title="View"
                      className="text-slate-500 hover:text-[#F97316] p-1 inline-flex ml-1">
                      <Eye size={14} />
                    </button>
                    {/* Direct Edit icon — same status-aware dispatcher as the
                        View-modal's Edit button. Saves the user a click for
                        Draft/Pending edits; for Approved+posted the confirm
                        warns about stock reversal before proceeding. */}
                    <button
                      data-testid={`edit-iv-${v.id}`}
                      onClick={async () => {
                        const status = v.status;
                        const posted = !!v.posted;
                        // ▸ Not posted → safe to edit directly. If currently
                        //   Pending, warn that the approval queue will reset
                        //   on save.
                        if (!posted) {
                          if (status === "Pending") {
                            if (!window.confirm(
                              `Voucher ${v.voucher_number} is currently in the approval queue.\n\n` +
                              "Editing will CANCEL the current approval request and start a fresh approval cycle with the new values. Continue?",
                            )) return;
                          }
                          openEdit(v);
                          return;
                        }
                        // ▸ Posted=true (Approved + stock issued) → loud
                        //   confirm + reverse stock + re-queue.
                        if (!window.confirm(
                          `Voucher ${v.voucher_number} is APPROVED and stock has already been issued.\n\n` +
                          "Editing will REVERSE all stock movements (restore batches & flip assets back to Available), and put the voucher back into the approval queue with the new values.\n\n" +
                          "Continue? Existing returnable lines that have NOT been returned yet are also wiped.",
                        )) return;
                        const t = toast.loading("Reversing stock movements…");
                        try {
                          await api.post(`/issue-vouchers/${v.id}/cancel-approval`);
                          const r = await api.get(`/issue-vouchers/${v.id}`);
                          toast.success(`Stock reversed. You can now edit ${v.voucher_number}.`, { id: t });
                          openEdit(r.data);
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to reverse for edit", { id: t });
                        }
                      }}
                      title={v.posted
                        ? "Edit — will reverse stock + re-queue"
                        : v.status === "Pending"
                          ? "Edit — will reset approval queue"
                          : "Edit voucher"}
                      className="text-slate-500 hover:text-blue-600 p-1 inline-flex ml-1">
                      <Pencil size={14} />
                    </button>
                    {isAdmin && (
                      <button
                        data-testid={`delete-iv-${v.id}`}
                        onClick={() => deleteVoucher(v)}
                        title={v.status === "Approved" && v.posted
                          ? "Reverse stock + delete voucher"
                          : "Delete voucher"}
                        className="text-slate-500 hover:text-red-600 p-1 inline-flex ml-1">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
      )}

      {viewing && <IssueVoucherView v={viewing} onViewList={closeViewToList} onClose={closeViewToDashboard} onReload={() => { setViewing(null); load(); }} onEdit={async (v) => {
        // Sprint 4.11.10 — Edit flow with status-aware confirm.
        // Draft   → silent open of edit form.
        // Pending → warn user that the L1/L2 queue will be reset.
        // ▸ Not posted → safe direct edit. Pending shows a soft confirm
        //   that the approval queue will be reset on save. Draft / Rejected /
        //   Approved-but-reversed all flow through here.
        const status = v.status;
        const posted = !!v.posted;
        if (!posted) {
          if (status === "Pending") {
            const ok = window.confirm(
              `Voucher ${v.voucher_number} is currently in the approval queue.\n\n` +
              "Editing will CANCEL the current approval request and start a fresh approval cycle with the new values. Continue?",
            );
            if (!ok) return;
          }
          setViewing(null);
          openEdit(v);
          return;
        }
        // ▸ Approved + posted: full reverse before opening edit form.
        const ok = window.confirm(
          `Voucher ${v.voucher_number} is APPROVED and stock has already been issued.\n\n` +
          "Editing will REVERSE all stock movements (restore batches & flip assets back to Available), and put the voucher back into the approval queue with the new values.\n\n" +
          "Continue? Existing returnable lines that have NOT been returned yet are also wiped.",
        );
        if (!ok) return;
        const t = toast.loading("Reversing stock movements…");
        try {
          await api.post(`/issue-vouchers/${v.id}/cancel-approval`);
          const r = await api.get(`/issue-vouchers/${v.id}`);
          toast.success(`Stock reversed. You can now edit ${v.voucher_number}.`, { id: t });
          setViewing(null);
          openEdit(r.data);
        } catch (err) {
          toast.error(err.response?.data?.detail || "Failed to reverse for edit", { id: t });
        }
      }} />}
      {exporting && <ExportVoucherMenu voucher={exporting} kind={exporting._kind} anchor={exporting._anchor} onClose={() => setExporting(null)} />}
      {bulkPrintOpen && (
        <BulkPrintModal
          ids={filtered.filter(v => selectedIds.has(v.id)).map(v => v.id)}
          onClose={() => setBulkPrintOpen(false)}
          onDone={() => { setBulkPrintOpen(false); setSelectedIds(new Set()); }}
        />
      )}
      {bulkOpen && <BulkIssueVoucherModal onClose={() => setBulkOpen(false)} onSuccess={() => { setBulkOpen(false); load(); }} />}
    </div>
  );
}


// ─────────── Bulk Issue Voucher upload modal (Sprint 4.1) ───────────
// Workflow:
//   1. Operator picks the target Project (single project per file).
//   2. Download template — backend returns an .xlsx pre-populated with
//      the project's branch + the Client Shipping Library reference.
//   3. Operator fills rows in Excel → uploads back here.
//   4. Backend validates ALL rows, auto-groups into IVs by
//      (Voucher Date + Issued-To Type + Name + Ship-To Label), FIFO-picks
//      batches, and creates vouchers (each submitted to the Approval
//      Matrix). All-or-nothing — any validation error blocks the whole
//      file and returns a structured error list keyed by row number.
export function BulkIssueVoucherModal({ onClose, onSuccess }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  // Sprint 4.11 — Source Warehouse picker. Required for the bulk path so
  // every created voucher carries a real warehouse_id (was the source of
  // most "orphan" records before).
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);  // {ok, created, voucher_numbers, errors}

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/projects?status=Active&limit=500");
        setProjects(Array.isArray(r.data) ? r.data : (r.data?.items || []));
      } catch (e) {
        toast.error("Could not load projects");
      }
    })();
  }, []);

  const proj = useMemo(
    () => projects.find(p => p.id === projectId) || null, [projects, projectId]);

  // Reload warehouse picker whenever the project changes (warehouse list is
  // branch-scoped). Auto-select the branch default if any.
  useEffect(() => {
    if (!proj?.branch_id) { setWarehouses([]); setWarehouseId(""); return; }
    api.get(`/warehouses/picker?branch_id=${proj.branch_id}`)
      .then(r => {
        const arr = Array.isArray(r.data) ? r.data.filter(w => w.is_active !== false) : [];
        setWarehouses(arr);
        // Auto-pick the branch default warehouse if available, else the first.
        const dflt = arr.find(w => w.is_default) || arr[0];
        setWarehouseId(dflt?.id || "");
      })
      .catch(() => { setWarehouses([]); setWarehouseId(""); });
    setResult(null);
  }, [proj?.branch_id]);

  async function downloadTemplate() {
    if (!projectId) { toast.error("Pick a project first"); return; }
    if (!warehouseId) { toast.error("Pick a source warehouse first"); return; }
    setDownloading(true);
    try {
      const res = await api.get(
        `/issue-vouchers-bulk/template?project_id=${encodeURIComponent(projectId)}&warehouse_id=${encodeURIComponent(warehouseId)}`,
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bulk_iv_template_${(proj?.project_code || projectId).replace(/[/\\\s]/g, "_")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill it and upload back.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not download template");
    } finally {
      setDownloading(false);
    }
  }

  async function upload() {
    if (!projectId) { toast.error("Pick a project first"); return; }
    if (!warehouseId) { toast.error("Pick a source warehouse first"); return; }
    if (!file) { toast.error("Choose an Excel file"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", projectId);
      fd.append("warehouse_id", warehouseId);
      const r = await api.post("/issue-vouchers-bulk/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data?.ok) {
        toast.success(`Created ${r.data.created} Issue Voucher${r.data.created === 1 ? "" : "s"}`);
      } else {
        toast.error(`${r.data?.errors?.length || 0} validation error${(r.data?.errors?.length || 0) === 1 ? "" : "s"} — no vouchers created`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Upload failed";
      setResult({ ok: false, errors: [{ row: "—", field: "—", message: detail }] });
      toast.error(detail);
    } finally {
      setUploading(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center pt-12 px-4" data-testid="bulk-iv-modal">
      <div className="bg-white w-full max-w-3xl shadow-2xl border border-slate-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Stores · Bulk Upload</div>
            <div className="text-lg font-bold text-slate-800">Bulk Create Issue Vouchers</div>
          </div>
          <button onClick={onClose} data-testid="bulk-iv-close-btn"
            className="text-slate-400 hover:text-slate-600 p-1"><X size={20} /></button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Step 1 — pick project */}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
              Step 1 · Project (single-project per file)
            </div>
            <SearchableSelect
              testid="bulk-iv-project-select"
              value={projectId}
              onChange={(id) => { setProjectId(id); setResult(null); }}
              items={projects}
              getKey={(p) => p.id}
              getLabel={(p) => `${p.project_code} — ${p.project_name}${p.branch_name ? ` (${p.branch_name})` : ""}`}
              getSubLabel={(p) => [p.client_name, p.status].filter(Boolean).join(" · ")}
              getSearchText={(p) => `${p.project_code || ""} ${p.project_name || ""} ${p.branch_name || ""} ${p.client_name || ""}`}
              placeholder="— Select Project —"
              searchPlaceholder="Search by code, name, branch or client…"
              emptyText="No matching projects"
              inputClassName="py-2 text-sm"
            />
            {proj && (
              <div className="text-[11px] text-slate-500 mt-1.5">
                Branch: <span className="font-medium text-slate-700">{proj.branch_name || "—"}</span>
                {" · "}Client: <span className="font-medium text-slate-700">{proj.client_name || "—"}</span>
              </div>
            )}
          </div>

          {/* Step 2 — pick source warehouse (Sprint 4.11) */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
              Step 2 · Source warehouse <span className="text-rose-600">*</span>
            </div>
            <select data-testid="bulk-iv-warehouse-select"
              disabled={!projectId || warehouses.length === 0}
              value={warehouseId}
              onChange={e => { setWarehouseId(e.target.value); setResult(null); }}
              className={inputCls}>
              <option value="">
                {!projectId ? "— Pick a project first —"
                  : warehouses.length === 0 ? "— No active warehouses in this branch —"
                  : "— Select source warehouse —"}
              </option>
              {warehouses.map(w => (
                <option key={w.id} value={w.id}>
                  {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · default" : ""}
                </option>
              ))}
            </select>
            <div className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
              <strong>All</strong> rows in this upload are issued from the warehouse above.
              FIFO batch picking is restricted to this warehouse, and every created voucher is stamped
              with its <code className="font-mono text-[10px] bg-slate-100 px-1">warehouse_id</code>.
              Switch warehouses by closing and re-opening this dialog.
            </div>
          </div>

          {/* Step 3 — download template */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
              Step 3 · Download template
            </div>
            <button onClick={downloadTemplate} disabled={!projectId || !warehouseId || downloading}
              data-testid="bulk-iv-download-template-btn"
              className="bg-slate-100 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed text-slate-700 px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 flex items-center gap-2">
              <FileDown size={13} /> {downloading ? "Downloading…" : "Download Template"}
            </button>
            <div className="text-[11px] text-slate-500 mt-1.5 leading-relaxed">
              The template includes 3 sheets: <strong>Issue Vouchers</strong> (data entry),
              <strong> Instructions</strong>, and <strong>Client Shipping Library</strong>
              {" "}(the list of every client&apos;s saved ship-to labels — copy-paste from here so they match exactly).
            </div>
          </div>

          {/* Step 4 — upload */}
          <div className="border-t border-slate-100 pt-4">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
              Step 4 · Upload filled .xlsx
            </div>
            <input data-testid="bulk-iv-file-input" type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="text-xs file:mr-3 file:py-2 file:px-4 file:border-0 file:bg-slate-100 file:text-slate-700 file:text-xs file:uppercase file:tracking-wider file:font-bold file:cursor-pointer hover:file:bg-slate-200 cursor-pointer" />
            <div className="text-[11px] text-slate-500 mt-1.5">
              Multiple rows are auto-grouped into vouchers by
              <strong> (Voucher Date + Issued-To Type + Issued-To Name + Ship-To Label)</strong>.
              {" "}All-or-nothing — if any row fails validation, NO vouchers are created.
            </div>
            <button onClick={upload} disabled={!projectId || !warehouseId || !file || uploading}
              data-testid="bulk-iv-upload-btn"
              className="mt-3 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
              <Upload size={13} /> {uploading ? "Uploading…" : "Upload & Create Vouchers"}
            </button>
          </div>

          {/* Result panel */}
          {result && (
            <div className={`border-t border-slate-100 pt-4`} data-testid="bulk-iv-result">
              {result.ok ? (
                <div className="bg-emerald-50 border border-emerald-200 px-4 py-3">
                  <div className="text-sm font-bold text-emerald-800 mb-1.5">
                    ✓ Created {result.created} Issue Voucher{result.created === 1 ? "" : "s"}
                  </div>
                  {(result.voucher_numbers || []).length > 0 && (
                    <div className="text-[11px] text-emerald-700 font-mono leading-relaxed">
                      {result.voucher_numbers.join(", ")}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-rose-50 border border-rose-200 px-4 py-3">
                  <div className="text-sm font-bold text-rose-800 mb-2 flex items-center gap-2">
                    <AlertTriangle size={14} /> {(result.errors || []).length} validation error{(result.errors || []).length === 1 ? "" : "s"} — no vouchers created
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="border-b border-rose-200">
                          <th className="text-left py-1 px-2 text-rose-700 font-bold uppercase tracking-wider">Row</th>
                          <th className="text-left py-1 px-2 text-rose-700 font-bold uppercase tracking-wider">Field</th>
                          <th className="text-left py-1 px-2 text-rose-700 font-bold uppercase tracking-wider">Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(result.errors || []).map((e, i) => (
                          <tr key={i} className="border-b border-rose-100">
                            <td className="py-1 px-2 font-mono text-rose-800">{e.row}</td>
                            <td className="py-1 px-2 text-rose-700">{e.field}</td>
                            <td className="py-1 px-2 text-rose-800">{e.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button onClick={onClose} data-testid="bulk-iv-cancel-btn"
            className="text-slate-600 hover:text-slate-800 px-4 py-2 text-xs uppercase tracking-wider font-bold">
            Close
          </button>
          {result?.ok && (
            <button onClick={onSuccess} data-testid="bulk-iv-done-btn"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 text-xs uppercase tracking-wider font-bold">
              Done — Refresh List
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}


// Jun-2026 (user request) — Bulk Print: combine selected issue vouchers into
// one PDF; the user picks the format (same three as the row export menu).
function BulkPrintModal({ ids, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  async function download(withAmount, compact = false) {
    setBusy(true);
    const t = toast.loading(`Building combined PDF for ${ids.length} voucher${ids.length > 1 ? "s" : ""}…`);
    try {
      const res = await api.post("/issue-vouchers/bulk-pdf",
        { voucher_ids: ids, with_amount: withAmount, compact },
        { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Material-Delivery-Vouchers-Bulk-${withAmount ? "with-amount" : "no-amount"}${compact ? "-compact" : ""}-${ids.length}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      const skipped = res.headers["x-skipped-vouchers"];
      if (skipped) {
        toast.warning(`PDF downloaded, but these vouchers could not be rendered and were skipped: ${skipped}`, { id: t, duration: 10000 });
      } else {
        toast.success(`Combined PDF downloaded (${ids.length} vouchers)`, { id: t });
      }
      onDone();
    } catch (e) {
      let msg = "Failed to build the combined PDF";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; }
      catch { if (e.response?.status) msg += ` (HTTP ${e.response.status})`; }
      toast.error(msg, { id: t });
    } finally { setBusy(false); }
  }
  const opts = [
    { testid: "bulk-fmt-with-amount", label: "Material Delivery Voucher — With Amount", hint: "Unit Rate, Line Value & Total Value included.", args: [true, false] },
    { testid: "bulk-fmt-no-amount", label: "Material Delivery Voucher — W/O Amount", hint: "Only Qty & descriptive columns.", args: [false, false] },
    { testid: "bulk-fmt-no-amount-compact", label: "Material Delivery Voucher — W/O Amount — Compact", hint: "40+ rows per page · no item codes · tight print layout.", args: [false, true] },
  ];
  return createPortal(
    <div className="fixed inset-0 w-screen h-screen bg-black/50 z-[9999] flex items-center justify-center p-4" data-testid="bulk-print-modal" style={{ position: "fixed", inset: 0 }}>
      <div className="bg-white w-full max-w-md shadow-2xl">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <h3 className="font-display text-lg font-bold">Bulk Print — {ids.length} voucher{ids.length > 1 ? "s" : ""}</h3>
            <p className="text-xs text-slate-500 mt-1">Pick a format — all selected vouchers are combined into ONE PDF in list order.</p>
          </div>
          <button data-testid="bulk-print-close" onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={16} /></button>
        </div>
        <div className="p-3">
          {opts.map(o => (
            <button key={o.testid} data-testid={o.testid} disabled={busy}
              onClick={() => download(...o.args)}
              className="w-full text-left px-3 py-3 hover:bg-slate-50 border border-slate-200 mb-2 disabled:opacity-50">
              <div className="text-xs font-bold text-slate-800">{o.label}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">{o.hint}</div>
            </button>
          ))}
        </div>
      </div>
    </div>, document.body);
}

export function ExportVoucherMenu({ voucher, kind, anchor, onClose }) {
  const isIssue = kind === "issue";
  const endpoint = isIssue ? "issue-vouchers" : "return-vouchers";
  const label = isIssue ? "Material Delivery Voucher" : "Material Return Voucher";

  async function download(withAmount, compact = false) {
    try {
      const res = await api.get(`/${endpoint}/${voucher.id}/voucher-pdf?with_amount=${withAmount}&compact=${compact}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label.replace(/\s/g, "-")}-${withAmount ? "with-amount" : "no-amount"}${compact ? "-compact" : ""}-${(voucher.voucher_number || voucher.id).replace(/[/\\]/g, "-")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded: ${label} (${withAmount ? "with amount" : "w/o amount"}${compact ? ", compact" : ""})`);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download");
    }
  }

  // Render via Portal to document.body so that ancestor `transform` (the page-wrapper's
  // `fade-up` animation) does not break `position: fixed`. Without the portal, fixed
  // elements become positioned relative to the transformed ancestor instead of the viewport.
  // Position the menu so its bottom-right corner is just above the Export button.
  const style = anchor
    ? { bottom: `${anchor.bottom}px`, right: `${anchor.right}px`, position: "fixed" }
    : { bottom: "80px", right: "20px", position: "fixed" };

  return createPortal(
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} data-testid="export-voucher-backdrop" />
      <div className="z-50 bg-white border border-slate-200 shadow-lg w-[270px]"
        style={style} data-testid="export-voucher-menu" role="menu">
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50">
          <div className="text-[9px] uppercase tracking-[0.18em] text-slate-500 font-bold">Export PDF</div>
          <div className="text-[11px] font-mono font-bold text-[#0B1F3A] truncate">{voucher.voucher_number}</div>
        </div>
        <button data-testid="export-with-amount" onClick={() => download(true)}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100">
          <div className="text-xs font-bold text-slate-800">{label} <span className="text-[#0B1F3A]">— With Amount</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">Includes Unit Rate &amp; Line Value.</div>
        </button>
        <button data-testid="export-no-amount" onClick={() => download(false)}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50 border-b border-slate-100">
          <div className="text-xs font-bold text-slate-800">{label} <span className="text-slate-500">— W/O Amount</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">Only Qty &amp; descriptive columns.</div>
        </button>
        <button data-testid="export-no-amount-compact" onClick={() => download(false, true)}
          className="w-full text-left px-3 py-2.5 hover:bg-slate-50">
          <div className="text-xs font-bold text-slate-800">{label} <span className="text-slate-500">— W/O Amount — Compact</span></div>
          <div className="text-[10px] text-slate-500 mt-0.5">40+ rows per page · no item codes · tight print layout.</div>
        </button>
      </div>
    </>,
    document.body,
  );
}

function IssueVoucherForm({ onClose, onViewList, onSaved, existing }) {
  const isEdit = !!existing?.id;
  const mastersRefreshKey = useMastersRefresh();
  const [branches, setBranches] = useState([]);
  // Sprint 4.3 Phase C — branch-scoped warehouse picker
  const [warehouses, setWarehouses] = useState([]);
  const [projects, setProjects] = useState([]);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);    // available stock batches (by branch)
  const [assets, setAssets] = useState([]);      // available assets (by branch)
  const [overdueCount, setOverdueCount] = useState(0);
  const [saving, setSaving] = useState(false);
  // After a successful save, surface a persistent green banner above the form
  // (in addition to the toast) so the user has unambiguous confirmation that
  // the voucher was created. The line items + issued-to fields are cleared
  // to make accidental double-submit impossible.
  const [lastSaved, setLastSaved] = useState(null);  // { voucher_number, id, status }
  const [projectSearch, setProjectSearch] = useState(null); // in-progress text; null = use bound label
  const today = new Date().toISOString().slice(0, 10);

  // Sprint 4.11.10 — seed form from `existing` voucher in edit mode.
  // We mirror every field that the create-flow expects so picker
  // selections stay stable. Lines are cloned to avoid mutating the
  // caller's copy when the user tweaks qty / removes a row.
  const seedForm = () => {
    if (isEdit) {
      const v = existing;
      return {
        voucher_date:        (v.voucher_date || today).slice(0, 10),
        reporting_period:    v.reporting_period || "",
        branch_id:           v.branch_id || "",
        branch_name:         v.branch_name || "",
        from_warehouse_id:   v.from_warehouse_id || "",
        from_warehouse_name: v.from_warehouse_name || "",
        project_id:          v.project_id || "",
        project_name:        v.project_name || "",
        issued_to_type:      v.issued_to_type || "Member",
        issued_to_id:        v.issued_to_id || "",
        issued_to_name:      v.issued_to_name || "",
        issued_to_free_text: v.issued_to_free_text || "",
        ship_to_name:        v.ship_to_name || "",
        ship_to_address:     v.ship_to_address || "",
        ship_to_city:        v.ship_to_city || "",
        ship_to_state:       v.ship_to_state || "",
        ship_to_pincode:     v.ship_to_pincode || "",
        ship_to_phone:       v.ship_to_phone || "",
        ship_to_gstin:       v.ship_to_gstin || "",
        ship_to_label:       v.ship_to_label || "",
        transport_mode:      v.transport_mode || "",
        vehicle_no:          v.vehicle_no || "",
        vehicle_type:        v.vehicle_type || "",
        transporter_name:    v.transporter_name || "",
        transporter_id:      v.transporter_id || "",
        lr_no:               v.lr_no || "",
        lr_date:             v.lr_date || "",
        distance_km:         v.distance_km || 0,
        dispatch_from:       v.dispatch_from || "",
        delivery_to:         v.delivery_to || "",
        reference_no:        v.reference_no || "",
        notes:               v.notes || "",
        line_items:          (v.line_items || []).map(li => ({ ...li })),
      };
    }
    return {
      voucher_date: today,
      reporting_period: today.slice(0, 7),
      branch_id: "", branch_name: "",
      from_warehouse_id: "", from_warehouse_name: "",
      project_id: "", project_name: "",
      issued_to_type: "Member",
      issued_to_id: "", issued_to_name: "",
      issued_to_free_text: "",
      ship_to_name: "", ship_to_address: "", ship_to_city: "",
      ship_to_state: "", ship_to_pincode: "", ship_to_phone: "", ship_to_gstin: "",
      ship_to_label: "",
      transport_mode: "", vehicle_no: "", vehicle_type: "",
      transporter_name: "", transporter_id: "",
      lr_no: "", lr_date: "", distance_km: 0,
      dispatch_from: "", delivery_to: "",
      reference_no: "",
      notes: "",
      line_items: [],
    };
  };
  const [form, setForm] = useState(seedForm);

  useEffect(() => {
    // Sprint 2026-Feb bug-fix — use Promise.allSettled so that a single
    // 403 on one picker endpoint does NOT blank out ALL dropdowns on the
    // Issue Voucher form. Root cause of the reported bug: users with only
    // `issue_vouchers.create` (no `items.view` / `members.view`) got a
    // 403 on `_pickable` calls, which caused Promise.all to reject and
    // every setter — including setBranches — was skipped. Backend picker
    // gates have been broadened via `require_permission_any` (see
    // server.py), and the frontend is now robust to future permission
    // gaps: each picker resolves independently.
    Promise.allSettled([
      api.get("/branches"),
      api.get("/projects-picker"),
      api.get("/members/_pickable"),
      api.get("/clients/picker"),
      api.get("/vendors/picker"),
      api.get("/items/_pickable"),
    ]).then(([b, p, m, c, v, i]) => {
      if (b.status === "fulfilled") setBranches(b.value.data || []);
      if (p.status === "fulfilled") setProjects(p.value.data || []);
      if (m.status === "fulfilled") setMembers(m.value.data || []);
      if (c.status === "fulfilled") setClients(c.value.data || []);
      if (v.status === "fulfilled") setVendors(v.value.data || []);
      if (i.status === "fulfilled") setItems(i.value.data || []);
    });
  }, [mastersRefreshKey]);

  // Reload batches/assets when branch changes
  useEffect(() => {
    if (!form.branch_id) { setBatches([]); setAssets([]); setWarehouses([]); return; }
    Promise.all([
      api.get(`/inventory/batches?branch_id=${form.branch_id}&include_depleted=false`),
      // Aug-2026 fix — the plain /assets list is PAGINATED (200/page), so
      // assets beyond the first page silently never loaded and pickers for
      // some warehouses looked empty. The picker endpoint is un-paginated,
      // branch-scoped and already filtered to Available.
      api.get("/assets-picker", { params: { branch_id: form.branch_id, status: "Available" } }),
      // Sprint 4.3 — branch-scoped warehouse picker (respects allowed_warehouse_ids)
      api.get(`/warehouses/picker?branch_id=${form.branch_id}`),
    ]).then(([b, a, w]) => {
      setBatches(b.data);
      // Server already scopes to Available + this branch (including assets
      // parked in this branch's warehouses with a drifted branch stamp).
      setAssets(a.data || []);
      const wlist = Array.isArray(w.data) ? w.data : [];
      setWarehouses(wlist);
      // Auto-select default when nothing is picked yet OR the current
      // pick is no longer in the branch-scoped list.
      const cur = wlist.find(x => x.id === form.from_warehouse_id);
      if (!cur) {
        const dflt = wlist.find(x => x.is_default) || wlist[0];
        setForm(p => ({
          ...p,
          from_warehouse_id:   dflt?.id || "",
          from_warehouse_name: dflt?.warehouse_name || "",
        }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.branch_id, mastersRefreshKey]);

  // When issued-to entity changes — fetch overdue count for warning
  useEffect(() => {
    if (!form.issued_to_id) { setOverdueCount(0); return; }
    api.get(`/returnable-register/_overdue-count?to_id=${form.issued_to_id}`)
       .then(r => setOverdueCount(r.data.overdue_count || 0));
  }, [form.issued_to_id]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  // Phase C-Lite — Reporting Period defaults to the issue month and stays
  // synced with the voucher date until the operator touches the field.
  const rpTouchedRef = useRef(false);
  useEffect(() => {
    if (isEdit || rpTouchedRef.current) return;
    const m = (form.voucher_date || "").slice(0, 7);
    if (/^\d{4}-(0[1-9]|1[0-2])$/.test(m) && m !== form.reporting_period) setF("reporting_period", m);
    // eslint-disable-next-line
  }, [form.voucher_date]);
  const setBranch = (bid) => {
    const br = branches.find(b => b.id === bid);
    setForm(p => ({ ...p, branch_id: bid, branch_name: br?.branch_name || "",
                    // Sprint 4.3 — clear WH on branch change; useEffect will auto-pick default
                    from_warehouse_id: "", from_warehouse_name: "",
                    line_items: [] }));
  };
  const setProject = (pid) => {
    const pr = projects.find(p => p.id === pid);
    setForm(p => ({ ...p, project_id: pid, project_name: pr?.project_name || pr?.name || "" }));
  };

  // Active list & key for selected issued-to entity
  const entityList = form.issued_to_type === "Member" ? members
                    : form.issued_to_type === "Client" ? clients
                    : form.issued_to_type === "Vendor" ? vendors : [];

  const setIssuedToType = (t) => {
    setForm(p => ({
      ...p, issued_to_type: t, issued_to_id: "", issued_to_name: "", issued_to_free_text: "",
      // Clear ship-to so it doesn't carry over wrong info
      ship_to_name: "", ship_to_address: "", ship_to_city: "",
      ship_to_state: "", ship_to_pincode: "", ship_to_phone: "", ship_to_gstin: "",
    }));
  };

  const setIssuedTo = (id) => {
    const ent = entityList.find(x => x.id === id);
    if (!ent) { setForm(p => ({ ...p, issued_to_id: "", issued_to_name: "" })); return; }
    // Auto-populate ship-to from entity's address (best-effort)
    const addrField = ent.billing_address || ent.address || "";
    setForm(p => ({
      ...p,
      issued_to_id: id,
      issued_to_name: ent.name || "",
      // Only auto-fill if currently blank, so the user can override
      ship_to_name: p.ship_to_name || ent.name || "",
      ship_to_address: p.ship_to_address || addrField || "",
      ship_to_phone: p.ship_to_phone || ent.phone || "",
      ship_to_gstin: p.ship_to_gstin || ent.gst_number || "",
    }));
  };

  const useProjectAddress = () => {
    if (!form.project_id) { toast.info("Pick a project first"); return; }
    const pr = projects.find(p => p.id === form.project_id);
    if (!pr) return;
    setForm(p => ({
      ...p,
      ship_to_name: pr.project_name || pr.name || "",
      ship_to_address: pr.location || pr.address || pr.site_address || "",
      ship_to_city: pr.city || "",
      ship_to_state: pr.state || "",
      ship_to_pincode: pr.pincode || "",
    }));
    toast.success("Ship-to filled from project");
  };
  const useBranchAddress = () => {
    const br = branches.find(b => b.id === form.branch_id);
    if (!br) { toast.info("Pick a branch first"); return; }
    setForm(p => ({
      ...p,
      ship_to_name: br.branch_name || "",
      ship_to_address: br.address || "",
      ship_to_city: br.city || "",
      ship_to_state: br.state || "",
      ship_to_pincode: br.pincode || "",
      ship_to_phone: br.phone || "",
      ship_to_gstin: br.gst_number || "",
    }));
    toast.success("Ship-to filled from branch");
  };

  function addLine(lineType) {
    setForm(p => ({
      ...p, line_items: [...p.line_items, {
        line_type: lineType,
        item_id: "", item_code: "", item_name: "", unit: "",
        batch_id: "", batch_code: "", quantity: 0, unit_rate: 0,
        asset_id: "", asset_code: "",
        issue_type: "Consumable", expected_return_date: "",
      }],
    }));
  }
  function removeLine(idx) {
    setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));
  }
  function updLine(idx, patch) {
    setForm(p => ({ ...p, line_items: p.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  }

  function pickBatch(idx, batchId) {
    const b = batches.find(x => x.id === batchId);
    if (!b) return;
    updLine(idx, {
      item_id: b.item_id, item_code: b.item_code, item_name: b.item_name, unit: b.unit,
      batch_id: b.id, batch_code: b.batch_code, unit_rate: b.unit_rate,
    });
  }
  function pickAsset(idx, assetId) {
    const a = assets.find(x => x.id === assetId);
    if (!a) return;
    updLine(idx, {
      item_id: a.item_id || "", item_code: a.item_code || "", item_name: a.name, unit: "Nos",
      asset_id: a.id, asset_code: a.asset_code, unit_rate: a.purchase_value,
    });
  }

  // batchSearch / assetSearch / batchLabel / assetLabel were the
  // glue for the legacy datalist-based picker. Sprint 4.11.8 swapped
  // those inputs for SearchableSelect which manages its own search
  // state internally, so those helpers are no longer referenced.

  const totalValue = useMemo(() => {
    return form.line_items.reduce((sum, li) => {
      if (li.line_type === "Material") return sum + (Number(li.quantity || 0) * Number(li.unit_rate || 0));
      return sum + Number(li.unit_rate || 0);
    }, 0);
  }, [form.line_items]);

  // For Client-typed issued-to, expose the selected client's shipping_addresses
  // so the user can pick one from a structured dropdown.
  const selectedClient = useMemo(
    () => form.issued_to_type === "Client"
      ? clients.find(c => c.id === form.issued_to_id) || null
      : null,
    [form.issued_to_type, form.issued_to_id, clients]
  );
  const clientShippingAddresses = selectedClient?.shipping_addresses || [];

  function pickClientShippingAddress(label) {
    const sa = clientShippingAddresses.find(x => x.label === label);
    if (!sa) {
      // Reset to manual entry
      setForm(p => ({ ...p, ship_to_label: "" }));
      return;
    }
    const addr = [sa.address_line1, sa.address_line2].filter(Boolean).join("\n");
    setForm(p => ({
      ...p,
      ship_to_label: sa.label,
      ship_to_name: sa.label || selectedClient?.name || "",
      ship_to_address: addr,
      ship_to_city: sa.city || "",
      ship_to_state: sa.state || "",
      ship_to_pincode: sa.pincode || "",
      ship_to_phone: sa.contact_phone || "",
      ship_to_gstin: sa.gst_number || selectedClient?.gst_number || "",
    }));
    toast.success(`Ship-to filled from "${sa.label}"`);
  }

  async function save(e) {
    e.preventDefault();
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    if (!form.from_warehouse_id) {
      toast.error("From Warehouse is mandatory. Pick a warehouse for the selected branch.");
      return;
    }
    if (!form.project_id) { toast.error("Project is mandatory"); return; }
    if (form.line_items.length === 0) { toast.error("Add at least one line"); return; }
    // Issued To validation
    if (form.issued_to_type === "Other") {
      if (!form.issued_to_free_text) { toast.error("Issued To (free text) is mandatory when type is Other"); return; }
    } else if (!form.issued_to_id) {
      toast.error(`Pick a ${form.issued_to_type}`); return;
    }
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (li.line_type === "Material") {
        if (!li.batch_id) { toast.error(`Line ${i+1}: pick a batch`); return; }
        if (!li.quantity || Number(li.quantity) <= 0) { toast.error(`Line ${i+1}: quantity must be > 0`); return; }
        const b = batches.find(x => x.id === li.batch_id);
        if (b && Number(li.quantity) > Number(b.remaining_qty)) {
          toast.error(`Line ${i+1}: qty ${li.quantity} exceeds available ${b.remaining_qty} ${b.unit}`); return;
        }
      } else {
        if (!li.asset_id) { toast.error(`Line ${i+1}: pick an asset`); return; }
      }
      if (li.issue_type === "Returnable" && !li.expected_return_date) {
        toast.error(`Line ${i+1}: expected return date required for Returnable lines`); return;
      }
    }
    setSaving(true);
    try {
      // Sprint 4.11.10 — edit mode uses PUT and produces a different
      // banner / toast message. The backend handles every status path
      // (Draft = quiet edit · Pending = wipe + re-submit · Approved =
      // reverse + edit + re-submit) so the FE doesn't need to branch.
      let data;
      if (isEdit) {
        const r = await api.put(`/issue-vouchers/${existing.id}`, form);
        data = r.data;
        const vn = data?.voucher_number || existing.voucher_number || "(unnumbered)";
        toast.success(`✓ Issue voucher ${vn} updated — awaiting fresh approval`, {
          duration: 7000,
          description: "Any prior stock movement was reversed and the voucher has been re-queued through the approval matrix.",
        });
      } else {
        const r = await api.post("/issue-vouchers", form);
        data = r.data;
        const vn = data?.voucher_number || "(unnumbered)";
        toast.success(`✓ Issue voucher ${vn} created — stock issued from FIFO batches`, {
          duration: 7000,
          description: "Material has been deducted from inventory. Refer to the green banner above the form for the voucher number.",
        });
      }
      const vn = data?.voucher_number || existing?.voucher_number || "(unnumbered)";
      setLastSaved({
        voucher_number: vn,
        id: data?.id || existing?.id || "",
        status: data?.status || "Approved",
        total_value: data?.total_value || 0,
        line_count: (data?.line_items || []).length,
        mode: isEdit ? "edit" : "create",
      });
      if (!isEdit) {
        // Reset the form's mutable bits so an accidental second submit cannot
        // recreate the same voucher. Keep project / branch / warehouse to
        // accelerate the next entry in a typical batch-of-issues workflow.
        setForm(f => ({
          ...f,
          voucher_number: "",
          line_items: [],
          issued_to_id: "",
          issued_to_name: "",
          issued_to_free_text: "",
          notes: "",
        }));
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  }

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="iv-form-overlay">
      <div className="bg-white w-full min-h-full">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <div>
            <h2 className="font-display text-2xl font-bold">{isEdit ? `Edit Issue Voucher — ${existing.voucher_number || existing.id?.slice(0, 8)}` : "New Issue Voucher"}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{isEdit ? "Save will re-queue the voucher through approval. Any prior stock movement has already been reversed." : "Material lines decrement batch qty · Asset lines flip status to Assigned. Returnable lines go to the register."}</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="iv-view-list-btn" onClick={onViewList}
              className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
              View List
            </button>
            <RefreshMastersButton testid="iv-refresh-masters-btn" />
            <button type="button" data-testid="iv-close-btn" onClick={onClose} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
          </div>
        </div>

        <form onSubmit={save} className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
          {/* Sprint 4.8 — Persistent "voucher saved" banner. Survives until
              the user starts editing line items again (or closes the form),
              eliminating the "did my save go through?" doubt that was
              causing operators to re-click Save and create duplicates. */}
          {lastSaved && (
            <div className="bg-emerald-50 border-2 border-emerald-400 px-4 py-3 flex items-start gap-3"
                 data-testid="iv-saved-banner">
              <CheckCircle2 size={20} className="text-emerald-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <div className="text-sm font-bold text-emerald-900">
                  ✓ Voucher {lastSaved.mode === "edit" ? "updated" : "created"} &nbsp;
                  <span className="font-mono text-emerald-700">{lastSaved.voucher_number}</span>
                </div>
                <div className="text-xs text-emerald-800 mt-0.5">
                  {lastSaved.line_count} line(s) · {formatINR(lastSaved.total_value)} · status <strong>{lastSaved.status}</strong>.
                  {lastSaved.mode === "edit"
                    ? " Prior stock movement was reversed and the voucher has been re-queued through the approval matrix."
                    : " Stock has been deducted from FIFO batches."}
                  {" "}Start a new entry below, click <em>View List</em> to see it, or close (X) to exit.
                </div>
              </div>
              <button type="button"
                      data-testid="iv-saved-banner-dismiss"
                      onClick={() => setLastSaved(null)}
                      className="text-emerald-700 hover:text-emerald-900 text-xs uppercase tracking-wider font-bold"
                      title="Dismiss confirmation">
                <X size={14} />
              </button>
            </div>
          )}

          {/* Overdue warning */}
          {overdueCount > 0 && (
            <div className="bg-amber-50 border border-amber-300 px-4 py-2.5 flex items-start gap-2" data-testid="overdue-warning">
              <AlertTriangle size={16} className="text-amber-700 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-900">
                <strong>{form.issued_to_name}</strong> has <strong>{overdueCount}</strong> overdue returnable item(s).
                You can still issue, but please follow up on the pending returns.
              </p>
            </div>
          )}

          {/* Header */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <Field label="Voucher No (auto · admin can override)">
              <VoucherNumberInput
                module="issue_voucher"
                value={form.voucher_number || ""}
                onChange={v => setF("voucher_number", v)}
                testidPrefix="iv-number"
              />
            </Field>
            <Field label="Voucher Date *">
              <input data-testid="iv-date" type="date" required value={form.voucher_date} onChange={e => setF("voucher_date", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Project Reporting Period">
              {isEdit && existing.status === "Approved" ? (
                <ApprovedReportingPeriod
                  resource="issue-vouchers"
                  docId={existing.id}
                  value={form.reporting_period || ""}
                  onSaved={p => setF("reporting_period", p)}
                  voucherLabel={`Issue Voucher ${existing.voucher_number || ""}`}
                  testid="iv-reporting-period"
                  className={inputCls}
                />
              ) : (
                <ReportingPeriodInput
                  value={form.reporting_period || ""}
                  onChange={v => { rpTouchedRef.current = true; setF("reporting_period", v); }}
                  testid="iv-reporting-period"
                  className={inputCls}
                />
              )}
            </Field>
            <Field label="Branch *">
              <select data-testid="iv-branch" required value={form.branch_id} onChange={e => setBranch(e.target.value)} className={inputCls}>
                <option value="">Select branch…</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </Field>
            <Field label="From Warehouse *">
              <select
                data-testid="iv-from-warehouse"
                required
                disabled={!form.branch_id}
                value={form.from_warehouse_id || ""}
                onChange={e => {
                  const w = warehouses.find(x => x.id === e.target.value);
                  setForm(p => ({
                    ...p,
                    from_warehouse_id:   e.target.value,
                    from_warehouse_name: w?.warehouse_name || "",
                    line_items: [],
                  }));
                }}
                className={inputCls}
              >
                <option value="">
                  {form.branch_id
                    ? (warehouses.length ? "Select warehouse…" : "No warehouse available")
                    : "Pick a branch first"}
                </option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Project *">
              {(() => {
                const projectsScoped = projects.filter(p => !form.branch_id || p.branch_id === form.branch_id);
                const projectLabel = (p) => `${p.project_code} · ${p.project_name || p.name || ""}`;
                const selected = projectsScoped.find(p => p.id === form.project_id);
                const boundLabel = selected ? projectLabel(selected) : (form.project_id ? form.project_name : "");
                const displayValue = projectSearch !== null ? projectSearch : boundLabel;
                return (
                  <>
                    <input data-testid="iv-project" type="text" list="iv-project-list" required
                      value={displayValue}
                      placeholder={`🔍 Search project by code or name (${projectsScoped.length})…`}
                      onChange={e => {
                        const raw = e.target.value || "";
                        setProjectSearch(raw);
                        const v = raw.trim().toLowerCase();
                        const match = projectsScoped.find(p => projectLabel(p).toLowerCase() === v);
                        if (match) { setProject(match.id); setProjectSearch(null); }
                        else if (!v) setProject("");
                      }}
                      onBlur={() => setProjectSearch(null)}
                      className={`${inputCls} ${!form.project_id ? "border-red-300" : ""}`} />
                    <datalist id="iv-project-list">
                      {projectsScoped.map(p => <option key={p.id} value={`${p.project_code} · ${p.project_name || p.name || ""}`} />)}
                    </datalist>
                  </>
                );
              })()}
            </Field>
            <Field label="Reference No">
              <input data-testid="iv-ref" value={form.reference_no} onChange={e => setF("reference_no", e.target.value)} className={inputCls} placeholder="GRN, work order, etc." />
            </Field>
          </div>

          {/* Issued To group */}
          <div className="bg-slate-50/40 border border-slate-200 px-4 py-3">
            <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-3">Issued To</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Type *">
                <select data-testid="iv-to-type" value={form.issued_to_type} onChange={e => setIssuedToType(e.target.value)} className={inputCls}>
                  <option value="Member">Member</option>
                  <option value="Client">Client</option>
                  <option value="Vendor">Vendor</option>
                  <option value="Other">Other (free text)</option>
                </select>
              </Field>
              {form.issued_to_type !== "Other" ? (
                <Field label={`${form.issued_to_type} *`} full>
                  {(() => {
                    const scoped = entityList.filter(x => !form.branch_id || !x.branch_id || x.branch_id === form.branch_id);
                    const isMember = form.issued_to_type === "Member";
                    return (
                      <SearchableSelect
                        value={form.issued_to_id}
                        onChange={(id) => setIssuedTo(id || "")}
                        items={scoped}
                        getKey={(x) => x.id}
                        getLabel={(x) => `${x.name}${isMember && x.designation ? " · " + x.designation : ""}${!isMember && x.gst_number ? " · " + x.gst_number : ""}`}
                        getSubLabel={(x) => isMember
                          ? [x.designation, x.mobile, x.email].filter(Boolean).join(" · ")
                          : [x.gst_number, x.city, x.state].filter(Boolean).join(" · ")}
                        getSearchText={(x) => `${x.name || ""} ${x.designation || ""} ${x.gst_number || ""} ${x.mobile || ""} ${x.email || ""} ${x.city || ""}`}
                        placeholder={`Search ${form.issued_to_type.toLowerCase()} (${scoped.length})…`}
                        searchPlaceholder={`Type name / ${isMember ? "designation / mobile" : "GSTIN / city"}…`}
                        required
                        testid="iv-to-entity"
                        inputClassName={inputCls}
                      />
                    );
                  })()}
                </Field>
              ) : (
                <Field label="Free Text *" full>
                  <input data-testid="iv-to-free-text" value={form.issued_to_free_text}
                    onChange={e => setF("issued_to_free_text", e.target.value)}
                    className={inputCls} placeholder="Enter recipient name" />
                </Field>
              )}
            </div>
          </div>

          {/* Ship To group */}
          <div className="bg-slate-50/40 border border-slate-200 px-4 py-3">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Ship To (Delivery Address)</h3>
              <div className="flex gap-2">
                <button type="button" data-testid="ship-to-use-project" onClick={useProjectAddress}
                  disabled={!form.project_id}
                  className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-white disabled:opacity-50">Use Project</button>
                <button type="button" data-testid="ship-to-use-branch" onClick={useBranchAddress}
                  disabled={!form.branch_id}
                  className="px-2 py-1 text-[10px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-white disabled:opacity-50">Use Branch</button>
              </div>
            </div>

            {/* Client shipping addresses picker — shown only when type=Client & client has saved ship-to addresses */}
            {selectedClient && clientShippingAddresses.length > 0 && (
              <div className="mb-3 bg-blue-50/40 border border-blue-200 px-3 py-2">
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-blue-800 mb-1.5 flex items-center justify-between">
                  <span>Pick from {selectedClient.name}&apos;s Saved Ship-To Addresses ({clientShippingAddresses.length})</span>
                  {form.ship_to_label && <span className="text-[#F97316] normal-case font-bold">· {form.ship_to_label}</span>}
                </div>
                <input data-testid="client-ship-to-picker" type="text" list="iv-client-ship-list"
                  placeholder={`🔍 Search ${clientShippingAddresses.length} saved address${clientShippingAddresses.length === 1 ? "" : "es"} (label / city / address / GSTIN)…`}
                  defaultValue=""
                  onChange={e => {
                    const v = (e.target.value || "").trim().toLowerCase();
                    const match = clientShippingAddresses.find(sa => {
                      const labelText = `${sa.label || ""}${sa.city ? " · " + sa.city : ""}${sa.is_default ? " · DEFAULT" : ""}`.trim().toLowerCase();
                      return labelText === v;
                    });
                    if (match) {
                      pickClientShippingAddress(match.label);
                      e.target.value = "";
                    }
                  }}
                  className={inputCls} />
                <datalist id="iv-client-ship-list">
                  {clientShippingAddresses.map((sa, i) => (
                    <option key={i} value={`${sa.label || `Address ${i + 1}`}${sa.city ? " · " + sa.city : ""}${sa.is_default ? " · DEFAULT" : ""}`} />
                  ))}
                </datalist>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Field label="Name">
                <input data-testid="ship-to-name" value={form.ship_to_name} onChange={e => setF("ship_to_name", e.target.value)} className={inputCls} placeholder="Site / consignee name" />
              </Field>
              <Field label="Phone">
                <input data-testid="ship-to-phone" value={form.ship_to_phone} onChange={e => setF("ship_to_phone", e.target.value)} className={inputCls} />
              </Field>
              <Field label="GSTIN (optional)" full>
                <input data-testid="ship-to-gstin" value={form.ship_to_gstin} onChange={e => setF("ship_to_gstin", e.target.value)} className={inputCls} maxLength={15} />
              </Field>
              <Field label="Address" full>
                <textarea data-testid="ship-to-address" rows={2} value={form.ship_to_address}
                  onChange={e => setF("ship_to_address", e.target.value)} className={inputCls}
                  placeholder="Street / building / landmark" />
              </Field>
              <Field label="City">
                <input data-testid="ship-to-city" value={form.ship_to_city} onChange={e => setF("ship_to_city", e.target.value)} className={inputCls} />
              </Field>
              <Field label="State">
                <input data-testid="ship-to-state" value={form.ship_to_state} onChange={e => setF("ship_to_state", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Pincode">
                <input data-testid="ship-to-pincode" value={form.ship_to_pincode} onChange={e => setF("ship_to_pincode", e.target.value)} className={inputCls} maxLength={6} />
              </Field>
            </div>
          </div>

          {/* Transportation (collapsed by default — fill as and when required) */}
          <details className="border border-slate-200 bg-slate-50/40 px-4 py-3" data-testid="iv-transport-section">
            <summary className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 cursor-pointer select-none">
              Transportation Details
              {(form.vehicle_no || form.transporter_name || form.lr_no) && (
                <span className="ml-2 text-[#F97316] normal-case">· populated</span>
              )}
            </summary>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
              <Field label="Transport Mode">
                <select data-testid="iv-transport-mode" value={form.transport_mode} onChange={e => setF("transport_mode", e.target.value)} className={inputCls}>
                  {["", "Road", "Rail", "Air", "Ship"].map(s => <option key={s} value={s}>{s || "—"}</option>)}
                </select>
              </Field>
              <Field label="Vehicle No">
                <input data-testid="iv-vehicle-no" value={form.vehicle_no} onChange={e => setF("vehicle_no", e.target.value)} className={inputCls} placeholder="e.g. WB-12-AB-1234" />
              </Field>
              <Field label="Vehicle Type">
                <select value={form.vehicle_type} onChange={e => setF("vehicle_type", e.target.value)} className={inputCls}>
                  {["", "Regular", "Over-Dimensional Cargo (ODC)"].map(s => <option key={s} value={s}>{s || "—"}</option>)}
                </select>
              </Field>
              <Field label="Transporter Name">
                <input value={form.transporter_name} onChange={e => setF("transporter_name", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Transporter ID / GSTIN">
                <input value={form.transporter_id} onChange={e => setF("transporter_id", e.target.value)} className={inputCls} maxLength={15} />
              </Field>
              <Field label="LR / Docket No">
                <input value={form.lr_no} onChange={e => setF("lr_no", e.target.value)} className={inputCls} />
              </Field>
              <Field label="LR Date">
                <input type="date" value={form.lr_date} onChange={e => setF("lr_date", e.target.value)} className={inputCls} />
              </Field>
              <Field label="Distance (km)">
                <input type="number" step="0.01" value={form.distance_km}
                  onChange={e => setF("distance_km", Number(e.target.value) || 0)} className={inputCls} />
              </Field>
              <Field label="Driver / Contact">
                <input value={form.dispatch_from} onChange={e => setF("dispatch_from", e.target.value)} className={inputCls} placeholder="Driver name / phone" />
              </Field>
              <Field label="Dispatched From">
                <input value={form.delivery_to} onChange={e => setF("delivery_to", e.target.value)} className={inputCls} placeholder="Pickup location" />
              </Field>
            </div>
          </details>

          {/* Notes */}
          <Field label="Notes" full>
            <textarea rows={1} value={form.notes} onChange={e => setF("notes", e.target.value)} className={inputCls} />
          </Field>

          {/* Lines */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">Line Items</h3>
              <div className="flex gap-2">
                <button type="button" data-testid="add-material-line" onClick={() => addLine("Material")}
                  disabled={!form.branch_id}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
                  <Plus size={11} /> Material
                </button>
                <button type="button" data-testid="add-asset-line" onClick={() => addLine("Asset")}
                  disabled={!form.branch_id}
                  className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50 disabled:opacity-50 flex items-center gap-1">
                  <Plus size={11} /> Asset
                </button>
              </div>
            </div>
            {!form.branch_id && (
              <p className="text-xs text-slate-500 italic flex items-center gap-1"><Info size={12} /> Select a branch first to load available batches and assets.</p>
            )}

            {form.line_items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="iv-lines-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>{["#", "Type", "Item / Batch / Asset", "Qty", "Rate", "Value", "Issue Type", "Expected Return", ""].map(h =>
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-2">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {form.line_items.map((li, idx) => {
                      const b = batches.find(x => x.id === li.batch_id);
                      const lineValue = li.line_type === "Material"
                        ? Number(li.quantity || 0) * Number(li.unit_rate || 0)
                        : Number(li.unit_rate || 0);
                      return (
                        <tr key={idx} className="border-b border-slate-100" data-testid={`iv-line-${idx}`}>
                          <td className="py-2 px-2 text-xs text-slate-500">{idx + 1}</td>
                          <td className="py-2 px-2">
                            <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${li.line_type === "Material" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>{li.line_type}</span>
                          </td>
                          <td className="py-2 px-2 min-w-[280px]">
                            {li.line_type === "Material" ? (
                              <SearchableSelect
                                testid={`iv-batch-${idx}`}
                                value={li.batch_id || ""}
                                onChange={(id) => {
                                  if (id) pickBatch(idx, id);
                                  else updLine(idx, { batch_id: "", batch_code: "", item_id: "", item_code: "", item_name: "", unit: "", unit_rate: 0 });
                                }}
                                items={batches.filter(b2 =>
                                  // Sprint 4.11.8 — restrict Material picker to
                                  // Inventory-category items only. Asset & Service
                                  // items must NOT appear here. (Legacy batches
                                  // with no item_type fall back to inclusion.)
                                  (!b2.item_type || b2.item_type === "Inventory")
                                  // Warehouse-scope (kept from datalist version).
                                  && (!form.from_warehouse_id || !b2.warehouse_id || b2.warehouse_id === form.from_warehouse_id)
                                )}
                                getKey={(b2) => b2.id}
                                getLabel={(b2) => `${b2.item_name || b2.item_code} · ${b2.batch_code}`}
                                getSubLabel={(b2) => `${b2.remaining_qty} ${b2.unit || ""} · ${formatINR(b2.unit_rate || 0)} · ${b2.item_code || ""}${b2.warehouse_name ? " · @ " + b2.warehouse_name : ""}`}
                                getSearchText={(b2) => `${b2.item_code || ""} ${b2.item_name || ""} ${b2.batch_code || ""} ${b2.warehouse_name || ""}`}
                                placeholder="— Select item / batch —"
                                searchPlaceholder="Search by item code / name / batch…"
                                emptyText="No matching inventory batch"
                                inputClassName="py-1.5 text-sm"
                              />
                            ) : (
                              <SearchableSelect
                                testid={`iv-asset-${idx}`}
                                value={li.asset_id || ""}
                                onChange={(id) => {
                                  if (id) pickAsset(idx, id);
                                  else updLine(idx, { asset_id: "", asset_code: "", item_id: "", item_code: "", item_name: "", unit: "", unit_rate: 0 });
                                }}
                                items={assets.filter(a =>
                                  !form.from_warehouse_id || !a.warehouse_id || a.warehouse_id === form.from_warehouse_id
                                )}
                                getKey={(a) => a.id}
                                getLabel={(a) => `${a.asset_code} · ${a.name}`}
                                getSubLabel={(a) => [a.serial_number ? `SN ${a.serial_number}` : "", formatINR(a.purchase_value || 0), a.warehouse_name ? `@ ${a.warehouse_name}` : ""].filter(Boolean).join(" · ")}
                                getSearchText={(a) => `${a.asset_code || ""} ${a.name || ""} ${a.serial_number || ""}`}
                                placeholder="— Select asset —"
                                searchPlaceholder="Search asset by code / name / SN…"
                                emptyText="No matching asset"
                                inputClassName="py-1.5 text-sm"
                              />
                            )}
                          </td>
                          <td className="py-2 px-2 w-24">
                            {li.line_type === "Material" ? (
                              <input data-testid={`iv-qty-${idx}`} type="number" step="0.01" value={li.quantity}
                                onChange={e => updLine(idx, { quantity: e.target.value })}
                                max={b?.remaining_qty}
                                className="w-full border border-slate-300 px-2 py-1.5 text-sm tabular text-right" />
                            ) : <span className="text-xs text-slate-400 italic">1</span>}
                          </td>
                          <td className="py-2 px-2 text-xs tabular text-slate-600">{formatINR(li.unit_rate || 0)}</td>
                          <td className="py-2 px-2 text-sm tabular font-bold">{formatINR(lineValue)}</td>
                          <td className="py-2 px-2 w-32">
                            <select data-testid={`iv-issuetype-${idx}`} value={li.issue_type} onChange={e => updLine(idx, { issue_type: e.target.value })}
                              className="w-full border border-slate-300 px-2 py-1.5 text-sm">
                              {ISSUE_TYPE.map(t => <option key={t}>{t}</option>)}
                            </select>
                          </td>
                          <td className="py-2 px-2 w-36">
                            {li.issue_type === "Returnable" ? (
                              <input data-testid={`iv-exp-return-${idx}`} type="date" value={li.expected_return_date || ""}
                                onChange={e => updLine(idx, { expected_return_date: e.target.value })}
                                className="w-full border border-slate-300 px-2 py-1.5 text-sm" />
                            ) : <span className="text-xs text-slate-400">—</span>}
                          </td>
                          <td className="py-2 px-2">
                            <button type="button" data-testid={`iv-del-line-${idx}`} onClick={() => removeLine(idx)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex justify-end gap-8 text-sm">
              <div>
                <span className="text-slate-500 mr-2">Total Value:</span>
                <span className="font-bold text-[#F97316] tabular text-base" data-testid="iv-total-value">{formatINR(totalValue)}</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-iv-btn" type="submit" disabled={saving} className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-60">
              {saving ? "Saving…" : "Save & Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function IssueVoucherView({ v, onClose, onViewList, onReload, onEdit }) {
  // Sprint 4.11.13 — scroll-lock + dynamic top offset via shared hook.
  // See /app/frontend/src/hooks/useViewOverlay.js for rationale.
  const topOffset = useViewOverlay();

  async function cancelApproval() {
    if (!confirm(`Reverse this approved voucher? Batch quantities will be restored and assets will be marked Available.`)) return;
    try {
      await api.post(`/issue-vouchers/${v.id}/cancel-approval`);
      toast.success("Voucher reversed");
      onReload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  }

  // Sprint 4.11.8 — Print/Download PDF for the current voucher (whether
  // it was created via single-form or bulk upload). Uses the same
  // `/voucher-pdf` endpoint as the list-view Export menu but skips the
  // menu altogether for a one-click experience inside the detail view.
  const [downloading, setDownloading] = useState(false);
  async function downloadPdf() {
    setDownloading(true);
    try {
      const r = await api.get(`/issue-vouchers/${v.id}/voucher-pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${v.voucher_number || v.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  }
  async function printPdf() {
    setDownloading(true);
    try {
      const r = await api.get(`/issue-vouchers/${v.id}/voucher-pdf`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      // Give the new tab a moment to render before firing print.
      if (w) setTimeout(() => { try { w.print(); } catch { /* user cancelled */ } }, 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  }
  return (
    <div
      className="fixed left-0 lg:left-64 right-0 bottom-0 bg-white z-40 overflow-y-auto"
      style={{ top: `${topOffset}px` }}
      data-testid="iv-view-overlay">
      <div className="bg-white w-full min-h-full">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight">{v.voucher_number}</h2>
            <p className="text-xs text-slate-500 mt-0.5">{(v.voucher_date || "").slice(0, 10)} · {v.branch_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={v.status} />
            {v.status === "Approved" && v.posted && (
              <button data-testid="iv-cancel-approval-btn" onClick={cancelApproval} className="px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border border-red-300 text-red-700 hover:bg-red-50">
                Reverse
              </button>
            )}
            {/* Sprint 4.11.10 — Edit button works on every status.
                Behaviour is delegated to onEdit() in the parent which
                handles the confirm-then-reverse-then-open dance. */}
            {onEdit && (
              <button data-testid="iv-edit-btn"
                onClick={() => onEdit(v)}
                title={v.status === "Approved" && v.posted
                  ? "Edit — will reverse stock + re-queue for approval"
                  : v.status === "Pending"
                    ? "Edit — will reset approval queue"
                    : "Edit voucher"}
                className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold text-slate-700 inline-flex items-center gap-1.5">
                <Pencil size={13} />
                Edit
              </button>
            )}
            <button data-testid="iv-print-pdf-btn"
              onClick={printPdf}
              disabled={downloading}
              title="Open PDF in a new tab and trigger Print"
              className="bg-slate-100 hover:bg-slate-200 disabled:opacity-50 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold text-slate-700 border border-slate-300 inline-flex items-center gap-1.5">
              <Printer size={13} />
              Print
            </button>
            <button data-testid="iv-download-pdf-btn"
              onClick={downloadPdf}
              disabled={downloading}
              title="Download voucher as PDF"
              className="bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold inline-flex items-center gap-1.5">
              <FileDown size={13} />
              {downloading ? "Generating…" : "Download PDF"}
            </button>
            <button type="button" onClick={onViewList}
              className="bg-white border border-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
              View List
            </button>
            <button onClick={onClose} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
          </div>
        </div>
        <div className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Issued To</div>
              <div className="font-medium">{v.issued_to_name || v.issued_to_free_text || "—"}</div>
              {v.issued_to_type && <div className="text-[10px] text-slate-500">{v.issued_to_type}</div>}
            </div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Project</div><div className="font-medium">{v.project_name || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Reference</div><div className="font-medium">{v.reference_no || "—"}</div></div>
            <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Total Value</div><div className="font-bold text-[#F97316] tabular">{formatINR(v.total_value || 0)}</div></div>
          </div>

          <VoucherScanLog vtype="iv" vid={v.id} />

          {(v.ship_to_name || v.ship_to_address) && (
            <div className="border border-slate-200 bg-slate-50/40 px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Ship To</div>
              <div className="text-sm text-slate-800">
                <div className="font-medium">{v.ship_to_name}</div>
                {v.ship_to_address && <div className="text-slate-700 whitespace-pre-line">{v.ship_to_address}</div>}
                <div className="text-xs text-slate-600">
                  {[v.ship_to_city, v.ship_to_state, v.ship_to_pincode].filter(Boolean).join(", ")}
                </div>
                {v.ship_to_phone && <div className="text-xs text-slate-500">Phone: {v.ship_to_phone}</div>}
                {v.ship_to_gstin && <div className="text-xs text-slate-500">GSTIN: {v.ship_to_gstin}</div>}
              </div>
            </div>
          )}

          {(v.vehicle_no || v.transporter_name || v.lr_no || v.transport_mode) && (
            <div className="border border-slate-200 bg-slate-50/40 px-4 py-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">Transportation</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs text-slate-700">
                {v.transport_mode && <div><span className="text-slate-400">Mode:</span> <strong>{v.transport_mode}</strong></div>}
                {v.vehicle_no && <div><span className="text-slate-400">Vehicle:</span> <strong>{v.vehicle_no}</strong></div>}
                {v.transporter_name && <div><span className="text-slate-400">Transporter:</span> <strong>{v.transporter_name}</strong></div>}
                {v.lr_no && <div><span className="text-slate-400">LR No:</span> <strong>{v.lr_no}</strong>{v.lr_date && ` · ${v.lr_date}`}</div>}
                {v.distance_km > 0 && <div><span className="text-slate-400">Distance:</span> {v.distance_km} km</div>}
                {v.dispatch_from && <div><span className="text-slate-400">Driver:</span> {v.dispatch_from}</div>}
              </div>
            </div>
          )}

          {v.notes && <div className="text-sm text-slate-600 bg-slate-50 px-3 py-2 italic">{v.notes}</div>}
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>{["#", "Type", "Item / Asset", "Batch", "Qty", "Rate", "Value", "Issue Type", "Expected Return"].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {(v.line_items || []).map((li, idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="py-2 px-3 text-xs text-slate-500">{idx + 1}</td>
                    <td className="py-2 px-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${li.line_type === "Material" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>{li.line_type}</span></td>
                    <td className="py-2 px-3">{li.line_type === "Material" ? li.item_name : <><span className="font-mono text-xs font-bold">{li.asset_code}</span> · {li.item_name}</>}</td>
                    <td className="py-2 px-3 text-xs font-mono text-slate-500">{li.batch_code || "—"}</td>
                    <td className="py-2 px-3 text-xs tabular">{li.line_type === "Material" ? `${li.quantity} ${li.unit}` : "1"}</td>
                    <td className="py-2 px-3 text-xs tabular">{formatINR(li.unit_rate || 0)}</td>
                    <td className="py-2 px-3 text-sm tabular font-bold">{formatINR(li.line_value || 0)}</td>
                    <td className="py-2 px-3 text-xs">{li.issue_type}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">{li.expected_return_date || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </div>
  );
}
